import React from 'react';
import PropTypes from 'prop-types';
import { connect } from 'react-redux';
import { StripeProvider, Elements } from 'react-stripe-elements';
import { isModalActive } from 'Store/reducers';
import { showModal, hideModal } from 'Store/ui/actions';
import { sendAnalyticsEventById } from 'Store/stats/actions';
import {
  getPlanByLicense,
  pricingOptions,
  getTrialLicenseByType,
} from 'Settings/pricing-options';
import { STRIPE_PUBLIC_KEY } from 'Settings';

import Loading from 'Components/Loading';
import ContentFrame from 'Components/ContentFrame';
import SectionHeader from 'Components/SectionHeader';
import ConnectedFormattedMessage from 'Containers/ConnectedFormattedMessage';
import SubscriptionPurchaseForm from 'Containers/paymentForms/SubscriptionPurchaseForm';
import PaymentPlanDetails from 'Components/PaymentPlanDetails';
import Lightbox from 'Components/Lightbox';
import PricingGrid from 'Components/PricingGrid';

class Payment extends React.PureComponent {
  constructor(props) {
    super(props);

    this.state = {
      selectedPlan: null,
    };

    // There are 4 options, but the last one is “custom” and users
    // can’t choose that for automated payment.
    this.planOptions = pricingOptions.slice(0, 3);
    this.handlePlanSelection = this.handlePlanSelection.bind(this);
  }

  componentDidMount() {
    this.setState({ selectedPlan: this.props.initialPlan });
    if (this.props.user && this.props.user.id) {
      this.props.sendAnalyticsEventById('viewed_subscription_page');
    }
  }

  componentDidUpdate(prevProps) {
    if (
      this.props.modalIsActive &&
      !prevProps.modalIsActive &&
      this.props.user &&
      this.props.user.id
    ) {
      this.props.sendAnalyticsEventById('viewed_pricing_grid');
    }
  }

  handlePlanSelection(type, shouldHideModal = true) {
    const licenseType = getTrialLicenseByType[type];
    const plan = getPlanByLicense(licenseType);
    this.setState({ selectedPlan: plan }, () => {
      if (shouldHideModal) this.props.hideModal('change-payment-plan');
    });
  }

  render() {
    const { user, school, modalIsActive, hasExpiredLicense } = this.props;
    const { selectedPlan } = this.state;

    if (!user || !selectedPlan) return <Loading />;

    return (
      <StripeProvider apiKey={STRIPE_PUBLIC_KEY}>
        <main className="Page__content">
          <ContentFrame
            size="large"
            pageType="with-right-sidebar"
            externalHeader={null}
            sidebar={
              <PaymentPlanDetails
                changePlanCallback={() =>
                  this.props.showModal('change-payment-plan')
                }
                plan={selectedPlan}
              />
            }
          >
            <SectionHeader
              title="Subscribe"
              type="payment"
              description={
                <p className="SectionHeader__description">
                  <ConnectedFormattedMessage id="payment_page.description" />
                </p>
              }
            />

            <Elements
              fonts={[
                {
                  family: 'AvenirLTStd-Roman',
                  // stripe-react-elements does not like it when you try to
                  // load a data file in Microsoft Edge. It’s unlikely they’ll
                  // Fix this, so we uploaded the file to the CDN.
                  // src: `url(${fontUrl}) format('woff')`,
                  src:
                    'url(https://schools-cdn.tinybop.com/stripe-fonts/33F6F0_2_0.woff)',
                },
              ]}
            >
              <SubscriptionPurchaseForm
                user={user}
                plan={selectedPlan}
                school={school}
                hasExpiredLicense={hasExpiredLicense}
              />
            </Elements>
          </ContentFrame>
          <Lightbox
            visible={modalIsActive}
            closeHandler={() => this.props.hideModal('change-payment-plan')}
            type="pricing-options"
            header="Select a plan"
          >
            <PricingGrid
              type="payment"
              upgrading={selectedPlan.availableUpgrade !== null}
              selected={selectedPlan.type}
              selectCallback={this.handlePlanSelection}
              showBanner={false}
              options={this.planOptions}
            />
          </Lightbox>
        </main>
      </StripeProvider>
    );
  }
}

const mapStateToProps = state => {
  return {
    modalIsActive: isModalActive(state, 'change-payment-plan'),
  };
};

Payment.propTypes = {
  /**
   * These modal props are Redux values for showing/hiding
   * the select-a-plan modal.
   */
  hideModal: PropTypes.func.isRequired,
  showModal: PropTypes.func.isRequired,
  modalIsActive: PropTypes.bool.isRequired,
  /**
   * The page needs to pass in an initial plan, though it can
   * be changed with the modal.
   */
  initialPlan: PropTypes.object.isRequired,
  /**
   * The user could either be the logged-in user or a school admin
   * who is trying to conver their expired trial into a paid license.
   */
  user: PropTypes.shape({
    auth_info: PropTypes.shape({
      username: PropTypes.string.isRequired,
    }),
    family_name: PropTypes.string,
    given_name: PropTypes.string,
  }),
  hasExpiredLicense: PropTypes.bool,
  /**
   * If the school is expired, we need to pass it into the payment form.
   */
  school: PropTypes.shape({
    id: PropTypes.number,
    name: PropTypes.string,
  }),
};

Payment.defaultProps = {
  hasExpiredLicense: false,
};

export default connect(mapStateToProps, {
  showModal,
  hideModal,
  sendAnalyticsEventById,
})(Payment);
